import React, { useState } from "react";
import {
    Avatar,
    Button,
    TextField,
    Typography,
    Box,
    Alert,
    InputAdornment,
    CircularProgress,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";
import { login } from "../services/authService";
import SwiftaLogo from "../assets/images/logo.png";
import { tokens } from "../theme/tokens";

// ---------- COLORS (flat navy + white, no gradient/glass/animation) ----------
const COLORS = {
    pageBg: tokens.color.brandDark,   // solid deep navy
    primary: tokens.color.brand,      // navy
    primaryHover: tokens.color.brandDark,
    brandText: "#fff",
    cardBg: "#ffffff",
    cardShadow: "0 10px 40px rgba(0,0,0,0.25)",
    subtitle: tokens.color.textSecondary,
    iconBg: tokens.color.surfaceAlt,
    iconCar: tokens.color.brand,
    lockBg: tokens.color.brand,
    copyright: tokens.color.textMuted,
    copyrightAccent: tokens.color.brand,
};
// ---------- END COLORS ----------

// Solid navy background (no animated gradient — zero continuous repaint).
const bgGradient = {
    background: COLORS.pageBg,
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
};

// Solid white card (no backdrop-filter blur).
const glassCard = {
    background: COLORS.cardBg,
    borderRadius: "16px",
    padding: "40px 32px",
    width: "100%",
    maxWidth: "420px",
    boxShadow: COLORS.cardShadow,
    margin: "90px 18px 32px",
};

// Brand text
const brandStyle = {
    fontWeight: 900,
    letterSpacing: 1.2,
    fontSize: "32px",
    color: COLORS.brandText,
};

const LoginPage: React.FC = () => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        try {
            const token = await login(username, password);
            localStorage.setItem("jwtToken", token);
            // Hard redirect (not soft navigate) so the root AuthProvider remounts
            // and fetches /admin/me — otherwise permissions/nav stay empty until refresh.
            window.location.href = "/";
        } catch {
            setError("Invalid username or password.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={bgGradient}>
            {/* Brand/Header */}
            <Box
                sx={{
                    position: "absolute",
                    top: 50,
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    zIndex: 99,
                }}
            >
                <Box
                    component="img"
                    src={SwiftaLogo}
                    alt="SwiftaGo"
                    sx={{
                        width: 65,
                        height: 65,
                        filter: "drop-shadow(0px 5px 25px rgba(0,0,0,0.13))",
                        mr: 1,
                    }}
                />
                <Typography component="span" sx={brandStyle}>
                    SwiftaGo Admin
                </Typography>
            </Box>

            {/* Login Panel */}
            <Box sx={glassCard}>
                <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
                    <Avatar
                        sx={{
                            mb: 2,
                            bgcolor: COLORS.lockBg,
                            width: 62,
                            height: 62,
                            color: "#fff",
                        }}
                    >
                        <LockOutlinedIcon fontSize="large" />
                    </Avatar>
                    <Typography component="h1" variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
                        Admin Login
                    </Typography>
                    <Typography variant="subtitle2" sx={{ mt: 1, color: COLORS.subtitle }}>
                        Secure portal for ride hailing operations
                    </Typography>
                </Box>

                <form onSubmit={handleLogin} style={{ width: "100%" }}>
                    <TextField
                        margin="normal"
                        fullWidth
                        label="Username"
                        variant="outlined"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        disabled={loading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Avatar
                                        sx={{
                                            bgcolor: COLORS.iconBg,
                                            width: 28,
                                            height: 28,
                                        }}
                                    >
                                        <DirectionsCarFilledIcon sx={{ color: COLORS.iconCar }} />
                                    </Avatar>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                            },
                        }}
                    />
                    <TextField
                        margin="normal"
                        fullWidth
                        name="password"
                        label="Password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading}
                        variant="outlined"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockOutlinedIcon sx={{ color: COLORS.iconCar }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                            },
                        }}
                    />
                    <Button
                        fullWidth
                        variant="contained"
                        type="submit"
                        disabled={loading}
                        sx={{
                            mt: 3,
                            py: 1.2,
                            fontWeight: 700,
                            fontSize: 16,
                            borderRadius: 2,
                            background: COLORS.primary,
                            "&:hover": {
                                background: COLORS.primaryHover,
                            },
                        }}
                    >
                        {loading ? <CircularProgress size={22} color="inherit" /> : "LOGIN"}
                    </Button>
                    {error && (
                        <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                            {error}
                        </Alert>
                    )}
                </form>

                <Typography
                    variant="caption"
                    sx={{
                        mt: 3,
                        display: "block",
                        color: COLORS.copyright,
                        textAlign: "center",
                    }}
                >
                    &copy; {new Date().getFullYear()} SwiftaGo. All rights reserved. <br />
                    <span style={{ color: COLORS.copyrightAccent }}>Enterprise-grade security.</span>
                </Typography>
            </Box>
        </Box>
    );
};

export default LoginPage;
